import React, { useState, useEffect } from 'react';
import { FaShoppingBag, FaSearch, FaFilter, FaDownload, FaEye, FaCheck, FaTimes, FaShippingFast } from 'react-icons/fa';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import LoadingSpinner from '../common/LoadingSpinner';
import Modal from '../common/Modal';
import { formatCurrency, formatDate } from '../../utils/helpers';

/**
 * Order Management component for schools
 * Allows viewing and managing ecommerce orders
 */
const OrderManagement = () => {
  const { showToast } = useToast();

  // Orders state
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Fetch orders on component mount
  useEffect(() => {
    fetchOrders();
  }, [currentPage, filterStatus]);

  // Fetch orders from API
  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await api.get('/ecommerce/orders', {
        params: {
          page: currentPage,
          limit: 10,
          status: filterStatus !== 'all' ? filterStatus : undefined,
          search: searchTerm || undefined
        }
      });
      
      setOrders(response.data.data || []);
      setTotalPages(response.data.pagination?.totalPages || 1);
    } catch (error) {
      console.error('Error fetching orders:', error);
      showToast('Failed to load orders', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle status filter change
  const handleStatusChange = (e) => {
    setFilterStatus(e.target.value);
    setCurrentPage(1);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle search form submit
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchOrders();
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // View order details
  const viewOrderDetails = (order) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
  };

  // Update order status
  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await api.put(`/ecommerce/orders/${orderId}/status`, {
        status: newStatus
      });
      
      showToast('Order status updated successfully', 'success');
      
      // Update local state
      setOrders(prevOrders => 
        prevOrders.map(order => 
          order._id === orderId 
            ? { ...order, status: newStatus } 
            : order
        )
      );
      
      // If viewing order details, update selected order
      if (selectedOrder && selectedOrder._id === orderId) {
        setSelectedOrder(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      showToast('Failed to update order status', 'error');
    }
  };

  // Generate and download orders report
  const exportOrders = () => {
    try {
      // This would be replaced with actual export functionality
      showToast('Exporting orders...', 'info');
      
      // Mock implementation - would be replaced with actual API call
      setTimeout(() => {
        showToast('Orders exported successfully', 'success');
      }, 1500);
    } catch (error) {
      console.error('Error exporting orders:', error);
      showToast('Failed to export orders', 'error');
    }
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'shipped':
        return 'bg-purple-100 text-purple-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <FaShoppingBag className="mr-2 text-primary" /> Order Management
          </h1>
          <p className="text-gray-600 mt-1">
            View and manage your school shop orders
          </p>
        </div>
        <div className="mt-4 md:mt-0">
          <button
            onClick={exportOrders}
            className="btn-secondary flex items-center"
          >
            <FaDownload className="mr-2" /> Export Orders
          </button>
        </div>
      </div>
      
      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-soft p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="md:w-1/4">
            <label htmlFor="statusFilter" className="block text-sm font-medium text-gray-700 mb-1">
              Filter by Status
            </label>
            <select
              id="statusFilter"
              value={filterStatus}
              onChange={handleStatusChange}
              className="form-select w-full"
            >
              <option value="all">All Orders</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          
          <div className="flex-1">
            <label htmlFor="searchOrders" className="block text-sm font-medium text-gray-700 mb-1">
              Search Orders
            </label>
            <form onSubmit={handleSearch} className="flex">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaSearch className="text-gray-400" />
                </div>
                <input
                  id="searchOrders"
                  type="text"
                  className="form-input pl-10 w-full"
                  placeholder="Search by order number, customer name, or product..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </div>
              <button type="submit" className="btn-primary ml-2">
                Search
              </button>
            </form>
          </div>
        </div>
      </div>
      
      {/* Orders Table */}
      {loading ? (
        <div className="bg-white rounded-lg shadow-soft p-8 flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-soft overflow-hidden">
          {orders.length === 0 ? (
            <div className="p-8 text-center">
              <FaShoppingBag className="mx-auto text-gray-300 text-5xl mb-4" />
              <h3 className="text-xl font-medium text-gray-600 mb-2">No Orders Found</h3>
              <p className="text-gray-500">
                {filterStatus !== 'all' || searchTerm 
                  ? 'Try adjusting your filters or search criteria'
                  : 'When customers place orders, they will appear here'}
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Order Number
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {orders.map((order) => (
                      <tr key={order._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="font-medium">{order.orderNumber}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {order.customer.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {formatDate(order.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap font-medium">
                          {formatCurrency(order.totalAmount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadgeClass(order.status)}`}>
                            {order.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <button
                            onClick={() => viewOrderDetails(order)}
                            className="text-primary hover:text-primary-dark mr-3"
                            title="View Details"
                          >
                            <FaEye />
                          </button>
                          
                          {order.status === 'pending' && (
                            <button
                              onClick={() => updateOrderStatus(order._id, 'processing')}
                              className="text-blue-600 hover:text-blue-800 mr-3"
                              title="Mark as Processing"
                            >
                              <FaCheck />
                            </button>
                          )}
                          
                          {order.status === 'processing' && (
                            <button
                              onClick={() => updateOrderStatus(order._id, 'shipped')}
                              className="text-purple-600 hover:text-purple-800 mr-3"
                              title="Mark as Shipped"
                            >
                              <FaShippingFast />
                            </button>
                          )}
                          
                          {['pending', 'processing'].includes(order.status) && (
                            <button
                              onClick={() => updateOrderStatus(order._id, 'cancelled')}
                              className="text-red-600 hover:text-red-800"
                              title="Cancel Order"
                            >
                              <FaTimes />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing page {currentPage} of {totalPages}
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`px-3 py-1 rounded border ${
                        currentPage === 1
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`px-3 py-1 rounded border ${
                        currentPage === totalPages
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
      
      {/* Order Details Modal */}
      {selectedOrder && (
        <Modal
          isOpen={showOrderModal}
          onClose={() => setShowOrderModal(false)}
          title={`Order #${selectedOrder.orderNumber}`}
          size="lg"
        >
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Order Information</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="mb-2">
                    <span className="text-sm text-gray-500">Date Placed:</span>
                    <p>{formatDate(selectedOrder.createdAt)}</p>
                  </div>
                  <div className="mb-2">
                    <span className="text-sm text-gray-500">Status:</span>
                    <p>
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadgeClass(selectedOrder.status)}`}>
                        {selectedOrder.status}
                      </span>
                    </p>
                  </div>
                  <div className="mb-2">
                    <span className="text-sm text-gray-500">Payment Method:</span>
                    <p>{selectedOrder.paymentMethod}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Payment Status:</span>
                    <p>
                      <span className={`px-2 py-1 text-xs rounded-full ${selectedOrder.isPaid ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {selectedOrder.isPaid ? 'Paid' : 'Pending'}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Customer Information</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="mb-2">
                    <span className="text-sm text-gray-500">Name:</span>
                    <p>{selectedOrder.customer.name}</p>
                  </div>
                  <div className="mb-2">
                    <span className="text-sm text-gray-500">Email:</span>
                    <p>{selectedOrder.customer.email}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Phone:</span>
                    <p>{selectedOrder.customer.phone}</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Shipping Address</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p>{selectedOrder.shippingAddress.street}</p>
                <p>{selectedOrder.shippingAddress.city}, {selectedOrder.shippingAddress.county}</p>
                <p>{selectedOrder.shippingAddress.postalCode}</p>
              </div>
            </div>
            
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Order Items</h3>
              <div className="border rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Product
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Price
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quantity
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {selectedOrder.items.map((item, index) => (
                      <tr key={index}>
                        <td className="px-4 py-3">
                          <div className="flex items-center">
                            <div className="h-10 w-10 flex-shrink-0 bg-gray-100 rounded">
                              {item.product.image && (
                                <img 
                                  src={item.product.image} 
                                  alt={item.product.name} 
                                  className="h-10 w-10 object-contain"
                                />
                              )}
                            </div>
                            <div className="ml-3">
                              <p className="text-sm font-medium text-gray-900">
                                {item.product.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {item.product.category}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-gray-500">
                          {formatCurrency(item.price)}
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-gray-500">
                          {item.quantity}
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                          {formatCurrency(item.price * item.quantity)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan="3" className="px-4 py-3 text-right text-sm font-medium text-gray-500">
                        Subtotal:
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                        {formatCurrency(selectedOrder.subtotal)}
                      </td>
                    </tr>
                    {selectedOrder.discountAmount > 0 && (
                      <tr>
                        <td colSpan="3" className="px-4 py-3 text-right text-sm font-medium text-gray-500">
                          Discount:
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-medium text-green-600">
                          -{formatCurrency(selectedOrder.discountAmount)}
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td colSpan="3" className="px-4 py-3 text-right text-sm font-medium text-gray-500">
                        Shipping:
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                        {formatCurrency(selectedOrder.shippingCost)}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan="3" className="px-4 py-3 text-right text-sm font-medium text-gray-500">
                        Tax:
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                        {formatCurrency(selectedOrder.taxAmount)}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan="3" className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                        Total:
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                        {formatCurrency(selectedOrder.totalAmount)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
            
            <div className="border-t pt-6 flex justify-between">
              <div>
                {['pending', 'processing', 'shipped'].includes(selectedOrder.status) && (
                  <div className="flex space-x-3">
                    {/* Status update buttons */}
                    {selectedOrder.status === 'pending' && (
                      <button
                        onClick={() => {
                          updateOrderStatus(selectedOrder._id, 'processing');
                          setShowOrderModal(false);
                        }}
                        className="btn-primary"
                      >
                        Mark as Processing
                      </button>
                    )}
                    
                    {selectedOrder.status === 'processing' && (
                      <button
                        onClick={() => {
                          updateOrderStatus(selectedOrder._id, 'shipped');
                          setShowOrderModal(false);
                        }}
                        className="btn-primary"
                      >
                        Mark as Shipped
                      </button>
                    )}
                    
                    {selectedOrder.status === 'shipped' && (
                      <button
                        onClick={() => {
                          updateOrderStatus(selectedOrder._id, 'delivered');
                          setShowOrderModal(false);
                        }}
                        className="btn-primary"
                      >
                        Mark as Delivered
                      </button>
                    )}
                    
                    {['pending', 'processing'].includes(selectedOrder.status) && (
                      <button
                        onClick={() => {
                          updateOrderStatus(selectedOrder._id, 'cancelled');
                          setShowOrderModal(false);
                        }}
                        className="btn-danger"
                      >
                        Cancel Order
                      </button>
                    )}
                  </div>
                )}
              </div>
              <button
                onClick={() => setShowOrderModal(false)}
                className="btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default OrderManagement; 